import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { controlResetClassName, cx } from './classNames';

// MUI's small Switch, which the app theme leaves as MUI draws it: a 40x24
// target holding a 26x10 track (the text colour at 38%, the accent at half
// strength when on) under a 16px thumb (white, the accent when on) that
// slides 16px, with a faint halo on hover; disabled dims the track to 12%
// and greys the thumb. The colours follow `checked` and `disabled` here
// rather than through CSS variants, whose order would decide the
// disabled-and-on state.
const BUTTON_CLASS_NAME = cx(
    controlResetClassName,
    'group relative inline-flex h-6 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full',
    'border-0 bg-transparent p-0 align-middle focus:outline-none focus-visible:outline',
    'focus-visible:outline-2 focus-visible:outline-ui-focus disabled:cursor-default',
);
const TRACK_CLASS_NAME = 'block h-2.5 w-[26px] rounded-[7px] transition-[background-color,opacity] duration-150';
const THUMB_CLASS_NAME = 'absolute left-1 top-1 block h-4 w-4 rounded-full transition-transform duration-150 '
    + 'shadow-[0_2px_1px_-1px_rgba(0,0,0,0.2),0_1px_1px_0_rgba(0,0,0,0.14),0_1px_3px_0_rgba(0,0,0,0.12)]';
// (Tailwind finds classes in the source text, so each stays one literal.)
const THUMB_HOVER_CLASS_NAMES = Object.freeze({
    on: 'group-hover:shadow-[0_0_0_4px_rgb(var(--c-accent)/0.08),0_2px_1px_-1px_rgba(0,0,0,0.2),0_1px_1px_0_rgba(0,0,0,0.14),0_1px_3px_0_rgba(0,0,0,0.12)]',
    off: 'group-hover:shadow-[0_0_0_4px_rgb(var(--c-text)/0.06),0_2px_1px_-1px_rgba(0,0,0,0.2),0_1px_1px_0_rgba(0,0,0,0.14),0_1px_3px_0_rgba(0,0,0,0.12)]',
});

function trackClassName(checked, disabled) {
    let opacity = checked ? 'opacity-50' : 'opacity-[0.38] dark:opacity-30';
    if (disabled) opacity = 'opacity-[0.12] dark:opacity-20';
    return cx(TRACK_CLASS_NAME, checked ? 'bg-accent' : 'bg-black dark:bg-white', opacity);
}

function thumbClassName(checked, disabled) {
    let color = checked ? 'bg-accent' : 'bg-white dark:bg-[#e0e0e0]';
    if (disabled && checked) {
        color = 'bg-[color-mix(in_srgb,rgb(var(--c-accent))_38%,#fff)] '
            + 'dark:bg-[color-mix(in_srgb,rgb(var(--c-accent))_45%,#000)]';
    } else if (disabled) {
        color = 'bg-[#f5f5f5] dark:bg-[#757575]';
    }
    return cx(
        THUMB_CLASS_NAME,
        color,
        checked ? 'translate-x-4' : 'translate-x-0',
        !disabled && THUMB_HOVER_CLASS_NAMES[checked ? 'on' : 'off'],
    );
}

/**
 * An on/off switch: a <button role="switch"> with aria-checked, toggled by
 * click, Enter or Space. `onChange(nextChecked, event)` reports a toggle;
 * name it with `aria-label`. Other props go to the button.
 */
const Switch = forwardRef(function Switch({
    checked = false,
    disabled = false,
    onChange,
    onClick,
    className,
    ...buttonProps
}, ref) {
    return (
        <button
            ref={ref}
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            className={cx(BUTTON_CLASS_NAME, className)}
            onClick={(event) => {
                onClick?.(event);
                if (!event.defaultPrevented) onChange?.(!checked, event);
            }}
            {...buttonProps}
        >
            <span aria-hidden="true" className={trackClassName(checked, disabled)} />
            <span aria-hidden="true" className={thumbClassName(checked, disabled)} />
        </button>
    );
});

Switch.propTypes = {
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    className: PropTypes.string,
};

export default Switch;
